import React, { useState } from 'react';
import { BookOpen, Clock, Award, CheckCircle, ArrowRight } from 'lucide-react';
import { academicPrograms, researchAreas } from '../data/mock';
import academicHero from "../assets/images/academicBG.webp";

const Academics = () => {
  const [selectedProgram, setSelectedProgram] = useState(null);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="gradient-hero-saffron py-20"
        style={{
          backgroundImage: `
      linear-gradient(to bottom, rgba(255,247,230,0.85),rgba(192, 119, 51, 0.9)),
      url('${academicHero}')
    `,
          backgroundPosition: "center",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground font-serif">
              Academics & Research
            </h1>
            <p className="text-xl text-muted-foreground">
              Comprehensive programs bridging tradition and modernity
            </p>
          </div>
        </div>
      </section>

      {/* Academic Programs */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4 font-serif">
                Academic Programs
              </h2>
              <p className="text-muted-foreground text-lg">
                Choose from our range of interdisciplinary courses
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {academicPrograms.map((program) => (
                <div
                  key={program.id}
                  className="bg-gradient-to-br from-white to-secondary/20 rounded-xl shadow-lg overflow-hidden hover-lift border border-primary/10"
                >
                  <div className="bg-gradient-to-r from-primary to-orange-600 p-6 text-white">
                    <h3 className="text-2xl font-bold mb-2">{program.title}</h3>
                    <p className="text-lg font-semibold">{program.name}</p>
                  </div>

                  <div className="p-6 space-y-4">
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-2 text-primary">
                        <Clock className="w-4 h-4" />
                        <span className="font-semibold">{program.duration}</span>
                      </div>
                      {program.credits && (
                        <div className="flex items-center gap-2 text-primary">
                          <Award className="w-4 h-4" />
                          <span className="font-semibold">{program.credits}</span>
                        </div>
                      )}
                    </div>

                    <p className="text-muted-foreground leading-relaxed">
                      {program.description}
                    </p>

                    <div className="bg-secondary/30 p-4 rounded-lg">
                      <p className="text-sm font-semibold text-foreground mb-2">
                        Eligibility:
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {program.eligibility}
                      </p>
                    </div>

                    <button
                      onClick={() => {
                        if (program.pdfLink) {
                          window.open(program.pdfLink, "_blank");
                        } else {
                          setSelectedProgram(program);
                        }
                      }}
                      className="w-full btn-outline-saffron flex items-center justify-center gap-2"
                    >
                      {program.pdfLink ? "View Details" : "View Curriculum"}
                    </button>
                    {program.guidelineLink && (
                      <button
                        onClick={() => window.open(program.guidelineLink, "_blank")}
                        className="w-full mt-2 btn-saffron flex items-center justify-center gap-2"
                      >
                        View Guidelines
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Research Focus Areas */}
      <section className="py-16 bg-secondary/20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4 font-serif">
                Research Focus Areas
              </h2>
              <p className="text-muted-foreground text-lg">
                Cutting-edge research in Indian Knowledge Systems
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {researchAreas.map((area, index) => (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl shadow-lg hover-lift"
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <BookOpen className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">
                    {area.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {area.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Learning Pedagogy */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-foreground mb-8 text-center font-serif">
              Learning Pedagogy
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                'Textual study and critical analysis of classical works',
                'Field visits and experiential learning',
                'Hands-on workshops (Yoga, Ayurveda, Arts)',
                'Student-led research projects and internships'
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 bg-gradient-to-br from-secondary/20 to-white p-4 rounded-lg border border-primary/10"
                >
                  <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <p className="text-foreground">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-primary to-orange-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h2 className="text-3xl font-bold font-serif">
              Ready to Begin Your Journey?
            </h2>
            <p className="text-xl text-white/90">
              Applications are now open for 2025-26 academic year
            </p>
            <button className="bg-white text-primary font-bold px-8 py-4 rounded-full hover:bg-gray-100 transition-all hover:scale-105 shadow-xl">
              Apply Now
            </button>
          </div>
        </div>
      </section>

      {/* Curriculum Modal */}
      {selectedProgram && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedProgram(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-primary to-orange-600 p-6 text-white">
              <h3 className="text-2xl font-bold">{selectedProgram.title}</h3>
              <p className="text-lg mt-2">{selectedProgram.name}</p>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <h4 className="text-xl font-bold text-foreground mb-4">
                  Curriculum
                </h4>
                <ul className="space-y-3">
                  {selectedProgram.curriculum.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                onClick={() => setSelectedProgram(null)}
                className="w-full btn-saffron"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Academics;
